import { c as _c } from "react/compiler-runtime";
// Shared types + primitive renderers for industry CMS blocks.
// No `'use client'` here — these primitives are pure render and can be
// imported from either server-rendered sections (IndustryPageSections.tsx)
// or interactive client islands (IndustryInteractiveSections.tsx).

import Image from 'next/image';
import type { LucideIcon } from 'lucide-react';
import { BookOpen, CircleDollarSign, ClipboardList, Eye, Flag, KeyRound, MapPinned, PlayCircle, ShieldCheck, ShipWheel, SquareCheckBig, TrafficCone, Truck, Waves } from 'lucide-react';
import { getRuntimeMediaUrl } from '@/lib/media-url';
import styles from '../IndustryPageSections/IndustryPageSections.module.css';

// -- types --

export interface IndustryImageRef {
  src: string;
  sourceSrc: string;
  alt: string;
  width: number;
  height: number;
}
export interface IndustryTextSegment {
  type: 'text';
  text: string;
}
export interface IndustryLinkSegment {
  type: 'link';
  text: string;
  href: string;
}
export interface IndustryLineBreakSegment {
  type: 'lineBreak';
}
export type IndustryRichTextSegment = IndustryTextSegment | IndustryLinkSegment | IndustryLineBreakSegment;
export interface IndustryIntroContent {
  blockId: string;
  eyebrow: string;
  title: string;
  body: string | IndustryRichTextSegment[];
}
export interface IndustryDecoderContent {
  blockId: string;
  eyebrow: string;
  title: string;
  body: string;
  image: IndustryImageRef;
}
export type IndustryBenefitIcon = 'lock' | 'eye' | 'checkbox' | 'flag' | 'regulation' | 'clipboard' | 'shield' | 'certificate' | 'delivery' | 'ship' | 'coin';
export interface IndustryBenefitCard {
  title: string;
  icon: IndustryBenefitIcon;
  body: IndustryRichTextSegment[];
}
export interface IndustryBenefitsContent {
  blockId: string;
  title: string;
  cards: IndustryBenefitCard[];
}
export interface IndustryTabContent {
  id: string;
  label: string;
  href: string;
  heading: string;
  secondaryHeading?: string;
  bullets: string[];
  secondaryBullets?: string[];
  image: IndustryImageRef;
  hoverImage?: IndustryImageRef;
  viewMoreHref?: string;
}
export interface IndustryTabbedContent {
  blockId: string;
  id?: string;
  eyebrow: string;
  title: string;
  body: string;
  sourceInteraction: 'tabs';
  tabs: IndustryTabContent[];
}
export interface IndustryGalleryCard {
  title: string;
  subtitle: string;
  href: string;
  image: IndustryImageRef;
}
export interface IndustryGalleryContent {
  blockId: string;
  title: string;
  body: string;
  sourceInteraction: 'slick.gallery_active';
  cards: IndustryGalleryCard[];
}
export interface IndustryRelatedCard {
  name: string;
  href: string;
  backgroundImage: string;
  icon: IndustryImageRef;
}
export interface IndustryRelatedContent {
  blockId: string;
  backgroundImage: string;
  cards: IndustryRelatedCard[];
}
export type IndustryResourceIcon = 'notepad' | 'map' | 'book' | 'video';
export interface IndustryResourceCard {
  title: string;
  icon: IndustryResourceIcon;
  body: IndustryRichTextSegment[];
}
export interface IndustryResourcesContent {
  blockId: string;
  title: string;
  paragraphs: string[];
  cta: {
    label: string;
    href: string;
  };
  cards: IndustryResourceCard[];
}
export interface IndustryFinalQuoteContent {
  blockId: string;
  id: string;
  eyebrow: string;
  title: string;
  subheading: string;
  body: string;
  backgroundImage: string;
  hubspotTitle: string;
  emptyFormMethod: 'get';
}
export type IndustryTabbedSectionVariant = 'layered' | 'choose';

// -- icon maps --

export const benefitIcons: Record<IndustryBenefitIcon, LucideIcon> = {
  lock: KeyRound,
  eye: Eye,
  checkbox: SquareCheckBig,
  flag: Flag,
  regulation: Waves,
  clipboard: ClipboardList,
  shield: ShieldCheck,
  certificate: TrafficCone,
  delivery: Truck,
  ship: ShipWheel,
  coin: CircleDollarSign
};
export const resourceIcons: Record<IndustryResourceIcon, LucideIcon> = {
  notepad: ClipboardList,
  map: MapPinned,
  book: BookOpen,
  video: PlayCircle
};

// -- primitive renderers --

export function CmsImage(t0) {
  const $ = _c(13);
  const {
    image,
    className,
    priority: t1,
    loading: t2,
    fetchPriority: t3,
    sizes: t4
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const loading = t2 === undefined ? "lazy" : t2;
  const fetchPriority = t3 === undefined ? "auto" : t3;
  const sizes = t4 === undefined ? "(min-width: 1024px) 20vw, (min-width: 768px) 33vw, 80vw" : t4;
  let t5;
  if ($[0] !== image.src) {
    t5 = getRuntimeMediaUrl(image.src);
    $[0] = image.src;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== className || $[3] !== fetchPriority || $[4] !== image.alt || $[5] !== image.height || $[6] !== image.sourceSrc || $[7] !== image.width || $[8] !== loading || $[9] !== priority || $[10] !== sizes || $[11] !== t5) {
    t6 = <Image src={t5} alt={image.alt} width={image.width} height={image.height} data-src={image.sourceSrc} className={className} priority={priority} loading={loading} fetchPriority={fetchPriority} sizes={sizes} unoptimized={true} />;
    $[2] = className;
    $[3] = fetchPriority;
    $[4] = image.alt;
    $[5] = image.height;
    $[6] = image.sourceSrc;
    $[7] = image.width;
    $[8] = loading;
    $[9] = priority;
    $[10] = sizes;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
export function RichText(t0) {
  const $ = _c(4);
  const {
    segments
  } = t0;
  let t1;
  if ($[0] !== segments) {
    t1 = segments.map(_temp);
    $[0] = segments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <>{t1}</>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(segment, index) {
  const key = `${segment.type}-${index}`;
  if (segment.type === "link") {
    return <a key={key} href={segment.href} className={styles.inlineLink}>{segment.text}</a>;
  }
  if (segment.type === "lineBreak") {
    return <br key={key} />;
  }
  return <span key={key}>{segment.text}</span>;
}
export function CtaLink(t0) {
  const $ = _c(5);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <strong>{children}</strong>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== href || $[3] !== t1) {
    t2 = <a href={href} className={styles.ctaButton} role="button">{t1}</a>;
    $[2] = href;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}